import { GlassKeyCard } from "@/components/GlassKeyCard";
import { MorningBriefing } from "@/components/MorningBriefing";
import { EveningDebrief } from "@/components/EveningDebrief";
import { NotesCard } from "@/components/NotesCard";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtRub } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle, Circle, Fire, TrendUp, TrendDown, CalendarBlank, Heartbeat, ArrowRight, Moon, WarningCircle, Warning, Info, ChartBar, Sparkle } from "@phosphor-icons/react";
import QuickStartCard from "@/components/QuickStartCard";
import MiniCalendar from "@/components/MiniCalendar";
import FamilyUnreadWidget from "@/components/FamilyUnreadWidget";

const priorityColors = { high: "text-[#ff3b30]", medium: "text-[#ffea00]", low: "text-muted-foreground" };
const priorityLabels = { high: "Высокий", medium: "Средний", low: "Низкий" };
const adviceIcon = {
  success: <CheckCircle size={18} weight="fill" className="text-[#00e676] shrink-0" />,
  warning: <Warning size={18} weight="fill" className="text-[#ffea00] shrink-0" />,
  alert: <WarningCircle size={18} weight="fill" className="text-[#ff3b30] shrink-0" />,
  info: <Info size={18} weight="fill" className="text-primary shrink-0" />,
};
const recColor = (v) => (v == null ? "" : v >= 67 ? "text-[#00e676]" : v >= 34 ? "text-[#ffea00]" : "text-[#ff3b30]");
const isSleepHabit = (name) => /сон|спат|лож|отбо|высып|медитац/i.test(name);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/report/weekly").then((r) => setReport(r.data)).catch(() => {});
  }, []);

  const toggleHabit = async (id) => {
    try {
      await api.post(`/habits/${id}/toggle`, { date: new Date().toISOString().slice(0, 10) });
      load();
    } catch { toast.error("Не удалось обновить привычку"); }
  };

  if (!data) return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 mt-20" data-testid="dashboard-loading">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="glass-card h-40 animate-pulse" />
      ))}
    </div>
  );

  const doneCount = data.habits.filter((h) => h.done_today).length;
  const wt = data.whoop_today;
  const lowRecovery = wt?.recovery != null && wt.recovery < 50;

  return (
    <div className="fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
          {new Date().toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">
          {(() => { const h = new Date().getHours(); return h < 5 ? "Доброй ночи" : h < 12 ? "Доброе утро" : h < 18 ? "Добрый день" : "Добрый вечер"; })()}, {user?.name} 👋
        </h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground" data-testid="daily-briefing">
          {(() => {
            const todayIso = new Date().toISOString().slice(0, 10);
            const meetings = data.events.filter((e) => e.date === todayIso && e.time).length;
            const openTasks = data.tasks.length;
            const focus = wt?.recovery == null ? null : wt.recovery >= 67 ? ["Тренируйтесь", "#00e676"] : wt.recovery >= 34 ? ["Лёгкая активность", "#ffea00"] : ["Восстанавливайтесь", "#ff3b30"];
            return (
              <>
                {focus && <span>Фокус: <span className="font-semibold" style={{ color: focus[1] }}>{focus[0]}</span> <span className="num">({Math.round(wt.recovery)}%)</span></span>}
                <span><span className="num font-semibold text-foreground">{meetings}</span> {meetings === 1 ? "встреча" : meetings > 1 && meetings < 5 ? "встречи" : "встреч"} сегодня</span>
                <span><span className="num font-semibold text-foreground">{openTasks}</span> {openTasks === 1 ? "задача" : openTasks > 1 && openTasks < 5 ? "задачи" : "задач"} в планнере</span>
              </>
            );
          })()}
        </div>
      </div>

      <MorningBriefing />
      <EveningDebrief />
      <NotesCard />

      <div className="mb-6">
        <QuickStartCard />
      </div>

      <Link to="/wrapped" data-testid="wrapped-banner"
        className="flex items-center gap-3 rounded-xl px-4 py-3 mb-5 border border-amber-300/25 hover:border-amber-300/50 transition-colors"
        style={{ background: "linear-gradient(90deg, rgba(255,200,60,0.12), rgba(169,112,255,0.08) 60%, transparent)" }}>
        <Sparkle size={18} weight="fill" className="text-amber-300 shrink-0" />
        <span className="text-sm font-semibold flex-1">Ваш {new Date().getFullYear()} в цифрах — годовой отчёт готов</span>
        <ArrowRight size={16} className="text-amber-300 shrink-0" />
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-5">
        <MiniCalendar />
        <FamilyUnreadWidget />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 stagger">
        {report && (
          <div className="col-span-1 md:col-span-3 lg:col-span-3 glass-card p-6" data-testid="weekly-report-widget">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={20} weight="duotone" className="text-primary" />
              <h2 className="font-heading font-semibold">Итоги недели</h2>
              <span className="text-xs text-muted-foreground num ml-auto">{report.period.start} — {report.period.end}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border-l-2 border-[#00e676] pl-3" data-testid="weekly-habits">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Привычки</div>
                <div className="font-heading text-2xl font-bold num">{report.habits.pct != null ? `${report.habits.pct}%` : "—"}</div>
                <div className="text-xs text-muted-foreground">
                  {report.habits.total > 0 ? `${report.habits.done} из ${report.habits.total} отметок` : "нет привычек"}
                  {report.habits.best && <span className="block truncate">лучшая: {report.habits.best.name} ({report.habits.best.count}/7)</span>}
                </div>
              </div>
              <div className="border-l-2 border-primary pl-3" data-testid="weekly-tasks">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Задачи</div>
                <div className="font-heading text-2xl font-bold num">{report.tasks_done}</div>
                <div className="text-xs text-muted-foreground">выполнено за неделю</div>
              </div>
              <div className="border-l-2 border-[#ffea00] pl-3" data-testid="weekly-budget">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Бюджет недели</div>
                <div className="font-heading text-lg font-bold num">
                  <span className="text-[#00e676]">+{fmtRub(report.budget.income)}</span>{" "}
                  <span className="text-[#ff3b30]">−{fmtRub(report.budget.expense)}</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {report.budget.top_category ? `больше всего: ${report.budget.top_category.name} (${fmtRub(report.budget.top_category.amount)})` : "нет расходов"}
                </div>
              </div>
              <div className="border-l-2 border-[#a970ff] pl-3" data-testid="weekly-whoop">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Здоровье (ср. за 7 дн.)</div>
                {report.whoop ? (
                  <>
                    <div className="font-heading text-lg font-bold num">
                      {report.whoop.recovery_avg != null ? `${Math.round(report.whoop.recovery_avg)}%` : "—"}{" "}
                      <span className="text-sm font-normal text-muted-foreground">восст.</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      сон {report.whoop.sleep_avg_h ?? "—"} ч · нагрузка {report.whoop.strain_avg ?? "—"}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground">Whoop не подключён</div>
                )}
              </div>
            </div>
          </div>
        )}

        {report && (
          <div className="col-span-1 md:col-span-3 lg:col-span-1 glass-card border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-6" data-testid="dashboard-ai-brief">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={18} weight="fill" className="text-primary" />
              <h2 className="font-heading font-semibold text-sm">Совет AI-тренера</h2>
            </div>
            {data.ai_brief ? (
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid="ai-brief-text">{data.ai_brief}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Разбор дня ещё не создан. Сгенерируйте его на вкладке «Здоровье».</p>
            )}
            <Link to="/health" className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="ai-brief-link">
              Полный разбор <ArrowRight size={12} />
            </Link>
          </div>
        )}

        <div className="col-span-1 md:col-span-2 glass-card p-6" data-testid="dashboard-habits-widget">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold">Привычки сегодня</h2>
            <span className="text-xs text-muted-foreground num">{doneCount}/{data.habits.length}</span>
          </div>
          {data.habits.length === 0 ? (
            <Link to="/habits" className="text-sm text-primary hover:underline" data-testid="dashboard-add-habit-link">Добавить первую привычку →</Link>
          ) : (
            <div className="space-y-2">
              {data.habits.slice(0, 5).map((h) => {
                const priority = lowRecovery && isSleepHabit(h.name) && !h.done_today;
                return (
                  <button
                    key={h.id}
                    onClick={() => toggleHabit(h.id)}
                    data-testid={`dashboard-habit-toggle-${h.id}`}
                    className={`w-full flex items-center gap-3 p-2 rounded-md hover:bg-secondary transition-colors text-left ${priority ? "border border-[#ffea00]/40 bg-[#ffea00]/5" : ""}`}
                  >
                    {h.done_today
                      ? <CheckCircle size={22} weight="fill" className="text-[#00e676]" />
                      : <Circle size={22} className="text-muted-foreground" />}
                    <span className={`flex-1 text-sm ${h.done_today ? "line-through text-muted-foreground" : ""}`}>{h.name}</span>
                    {priority && (
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[#ffea00]" data-testid={`habit-priority-badge-${h.id}`}>
                        <Moon size={12} weight="fill" /> Важно сегодня
                      </span>
                    )}
                    {h.streak > 0 && (
                      <span className="flex items-center gap-1 text-xs text-[#ffea00] num"><Fire size={14} weight="fill" />{h.streak}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="col-span-1 glass-card p-6" data-testid="dashboard-budget-widget">
          <h2 className="font-heading font-semibold mb-4">Бюджет · {data.budget.month}</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <TrendUp size={18} className="text-[#00e676]" />
              <span className="text-muted-foreground flex-1">Доходы</span>
              <span className="num text-[#00e676]">{fmtRub(data.budget.income)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <TrendDown size={18} className="text-[#ff3b30]" />
              <span className="text-muted-foreground flex-1">Расходы</span>
              <span className="num text-[#ff3b30]">{fmtRub(data.budget.expense)}</span>
            </div>
            <div className="pt-3 border-t border-border/60">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Баланс</div>
              <div className="font-heading text-2xl font-bold num" data-testid="dashboard-balance">{fmtRub(data.budget.balance)}</div>
            </div>
          </div>
          <Link to="/budget" className="mt-4 inline-flex items-center gap-1 text-xs text-primary hover:underline">Подробнее <ArrowRight size={12} /></Link>
        </div>

        <div className="col-span-1 glass-card p-6" data-testid="dashboard-health-widget">
          <div className="flex items-center gap-2 mb-4">
            <Heartbeat size={20} weight="duotone" className="text-primary" />
            <h2 className="font-heading font-semibold">Здоровье</h2>
          </div>
          {wt ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className={`font-heading text-xl font-bold num ${recColor(wt.recovery)}`}>{wt.recovery != null ? `${Math.round(wt.recovery)}%` : "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Восст.</div>
                </div>
                <div>
                  <div className="font-heading text-xl font-bold num text-primary">{wt.strain ?? "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Нагрузка</div>
                </div>
                <div>
                  <div className="font-heading text-xl font-bold num text-[#a970ff]">{wt.sleep_performance != null ? `${Math.round(wt.sleep_performance)}%` : "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Сон</div>
                </div>
              </div>
              {wt.advice && (
                <div className="flex gap-2 pt-3 border-t border-border/40" data-testid="dashboard-whoop-advice">
                  {adviceIcon[wt.advice.level]}
                  <div>
                    <div className="text-xs font-semibold mb-0.5">{wt.advice.title}</div>
                    <div className="text-[11px] text-muted-foreground leading-relaxed">{wt.advice.text}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Фитнес-трекер не подключён</p>
          )}
          <Link to="/health" className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline">Открыть <ArrowRight size={12} /></Link>
        </div>

        <div className="col-span-1 md:col-span-2 glass-card p-6" data-testid="dashboard-tasks-widget">
          <h2 className="font-heading font-semibold mb-4">Активные задачи</h2>
          {data.tasks.length === 0 ? (
            <Link to="/planner" className="text-sm text-primary hover:underline">Добавить задачу →</Link>
          ) : (
            <div className="space-y-2">
              {data.tasks.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center gap-3 text-sm p-2 rounded-md">
                  <span className={`text-xs uppercase tracking-wider font-semibold ${priorityColors[t.priority]}`}>
                    {priorityLabels[t.priority]}
                  </span>
                  <span className="flex-1 truncate">{t.title}</span>
                  {t.due_date && <span className="text-xs text-muted-foreground num">{t.due_date}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-1 md:col-span-2 glass-card p-6" data-testid="dashboard-events-widget">
          <div className="flex items-center gap-2 mb-4">
            <CalendarBlank size={20} weight="duotone" className="text-primary" />
            <h2 className="font-heading font-semibold">Ближайшие события</h2>
          </div>
          {data.events.length === 0 ? (
            <Link to="/calendar" className="text-sm text-primary hover:underline">Добавить событие →</Link>
          ) : (
            <div className="space-y-2">
              {data.events.map((e) => (
                <div key={`${e.id}-${e.date}`} className="flex items-center gap-3 text-sm p-2">
                  <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                  <span className="flex-1 truncate">{e.title}</span>
                  <span className="text-xs text-muted-foreground num">{e.date}{e.time ? ` · ${e.time}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <GlassKeyCard />
    </div>
  );
}
